export type OsmQueryType = "node" | "way" | "relation" | "all"

export interface CustomQuery {
  key: string
  value: string
  type: OsmQueryType
}

const QUERY_TYPES = new Set<OsmQueryType>(["node", "way", "relation", "all"])

export function normalizeOsmQueryType(raw: unknown): OsmQueryType {
  const s = String(raw ?? "").trim().toLowerCase()
  if (QUERY_TYPES.has(s as OsmQueryType)) return s as OsmQueryType
  return "way"
}

/**
 * Parse one Overpass-style tag filter into a stored custom query.
 * Accepts `landuse=residential`, `[landuse=residential]`,
 * `way[landuse=residential]`, and quoted Overpass forms.
 */
export function parseOsmTagFilter(raw: string): CustomQuery | null {
  const s = raw.trim()
  if (!s) return null

  const typed = s.match(
    /^(node|way|relation|all)\s*\[\s*"?([^"=\[\]]+)"?\s*(?:=\s*"?([^"\]]*)"?\s*)?\]$/i
  )
  if (typed) {
    return {
      type: typed[1].toLowerCase() as OsmQueryType,
      key: typed[2].trim(),
      value: (typed[3] ?? "").trim(),
    }
  }

  const bracket = s.match(/^\[\s*"?([^"=\[\]]+)"?\s*(?:=\s*"?([^"\]]*)"?\s*)?\]$/)
  if (bracket) {
    return {
      type: "way",
      key: bracket[1].trim(),
      value: (bracket[2] ?? "").trim(),
    }
  }

  const eq = s.match(/^([^=\[\]]+)=(.*)$/)
  if (eq) {
    const key = eq[1].trim()
    if (key) {
      return {
        type: "way",
        key,
        value: eq[2].trim().replace(/^["']|["']$/g, ""),
      }
    }
  }

  if (/^[a-z_][a-z0-9_:-]*$/i.test(s)) {
    return { type: "way", key: s, value: "" }
  }

  return null
}

function queryFromObject(obj: Record<string, unknown>): CustomQuery | null {
  const keyRaw = obj.key ?? obj.k ?? obj.tag
  if (typeof keyRaw === "string" && keyRaw.trim()) {
    const valueRaw = obj.value ?? obj.v ?? obj.val ?? ""
    return {
      key: keyRaw.trim(),
      value: String(valueRaw ?? "").trim(),
      type: normalizeOsmQueryType(obj.type ?? obj.element ?? obj.osmType),
    }
  }

  const entries = Object.entries(obj).filter(
    ([k]) => !["type", "element", "osmType"].includes(k)
  )
  if (entries.length === 1) {
    const [k, v] = entries[0]
    if (k && (typeof v === "string" || typeof v === "number" || v == null)) {
      return {
        key: k,
        value: v == null ? "" : String(v).trim(),
        type: normalizeOsmQueryType(obj.type),
      }
    }
  }

  return null
}

function collectQueryItems(value: unknown): unknown[] | { error: string } {
  if (value == null || value === "" || value === false) return []

  if (Array.isArray(value)) return value

  if (typeof value === "string") {
    const trimmed = value.trim()
    if (!trimmed) return []

    const asTag = parseOsmTagFilter(trimmed)
    if (asTag) return [asTag]

    if (
      (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
      (trimmed.startsWith("{") && trimmed.endsWith("}"))
    ) {
      try {
        return collectQueryItems(JSON.parse(trimmed))
      } catch {
        // Fall through and split on separators.
      }
    }

    return trimmed.split(/[\n;]+/).map(s => s.trim()).filter(Boolean)
  }

  if (typeof value === "object") {
    const obj = value as Record<string, unknown>
    if (obj.key != null || obj.k != null || obj.tag != null) return [obj]

    const type = obj.type
    const items: unknown[] = []
    for (const [k, v] of Object.entries(obj)) {
      if (k === "type" || k === "element" || k === "osmType") continue
      items.push({ key: k, value: v, type })
    }
    return items
  }

  return {
    error:
      'Invalid OSM custom query. Pass [{key, value, type}] e.g. [{"key":"landuse","value":"residential","type":"way"}], or "landuse=residential".',
  }
}

function parseQueryItem(
  item: unknown
): { ok: true; query: CustomQuery } | { ok: false; error: string } {
  if (item == null || item === "") {
    return { ok: false, error: "Empty OSM query." }
  }
  if (typeof item === "string") {
    const parsed = parseOsmTagFilter(item)
    if (!parsed) {
      return {
        ok: false,
        error:
          `Could not parse OSM query ${JSON.stringify(item)}. Use {key, value, type} ` +
          `(type is node|way|relation|all) or "key=value" / "[key=value]".`,
      }
    }
    return { ok: true, query: parsed }
  }
  if (typeof item === "object" && !Array.isArray(item)) {
    const query = queryFromObject(item as Record<string, unknown>)
    if (!query) {
      return {
        ok: false,
        error:
          `Could not parse OSM query object ${JSON.stringify(item)}. Expected {key, value, type}.`,
      }
    }
    return { ok: true, query }
  }
  return {
    ok: false,
    error:
      `Could not parse OSM query ${JSON.stringify(item)}. Expected {key, value, type} or "key=value".`,
  }
}

export function parseOsmCustomQueryValue(
  value: unknown
): { ok: true; queries: CustomQuery[] } | { ok: false; error: string } {
  const collected = collectQueryItems(value)
  if (!Array.isArray(collected)) return { ok: false, error: collected.error }

  const queries: CustomQuery[] = []
  for (const item of collected) {
    if (item == null || item === "") continue
    const parsed = parseQueryItem(item)
    if (!parsed.ok) return parsed
    queries.push(parsed.query)
  }
  return { ok: true, queries }
}

/** Best-effort read of stored node data so the Build Query UI never crashes. */
export function normalizeOsmCustomQueries(data: unknown): CustomQuery[] {
  const parsed = parseOsmCustomQueryValue(data)
  if (parsed.ok) return parsed.queries

  if (!Array.isArray(data)) return []
  const recovered: CustomQuery[] = []
  for (const item of data) {
    const parsedItem = parseQueryItem(item)
    if (parsedItem.ok) recovered.push(parsedItem.query)
  }
  return recovered
}
